import React, { createContext, useCallback, useContext, useMemo, useState } from "react";

const ToastContext = createContext({ showToast: () => {} });

function toneStyles(type) {
  if (type === "success") {
    return {
      border: "border-emerald-200",
      bg: "bg-emerald-50",
      text: "text-emerald-800",
      dot: "bg-emerald-500",
    };
  }
  if (type === "error") {
    return {
      border: "border-red-200",
      bg: "bg-red-50",
      text: "text-red-800",
      dot: "bg-red-500",
    };
  }
  return {
    border: "border-slate-200",
    bg: "bg-white",
    text: "text-slate-800",
    dot: "bg-slate-400",
  };
}

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const showToast = useCallback(
    ({ type = "info", title, message, duration = 3200 }) => {
      const id = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      setToasts((prev) => [...prev, { id, type, title: title || "", message: message || "" }]);
      window.setTimeout(() => removeToast(id), duration);
    },
    [removeToast]
  );

  const value = useMemo(() => ({ showToast }), [showToast]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="pointer-events-none fixed right-4 top-4 z-[70] flex w-full max-w-sm flex-col gap-2">
        {toasts.map((toast) => {
          const styles = toneStyles(toast.type);
          return (
            <div
              key={toast.id}
              className={`pointer-events-auto rounded-xl border ${styles.border} ${styles.bg} p-3 shadow-lg`}
              role="status"
              aria-live="polite"
            >
              <div className="flex items-start gap-2">
                <span className={`mt-1 h-2.5 w-2.5 rounded-full ${styles.dot}`} />
                <div className="min-w-0 flex-1">
                  {!!toast.title && (
                    <p className={`text-sm font-semibold ${styles.text}`}>{toast.title}</p>
                  )}
                  {!!toast.message && (
                    <p className={`text-xs ${styles.text} opacity-90 whitespace-pre-line`}>
                      {toast.message}
                    </p>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => removeToast(toast.id)}
                  className="rounded p-0.5 text-slate-500 hover:bg-black/5"
                  aria-label="Close notification"
                >
                  ✕
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  return useContext(ToastContext);
}
